import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Shield, LogOut, UserCheck, KeyRound, Bot, Wallet, Wrench, Receipt, FileText, FileSpreadsheet, RefreshCcw, Trophy, List, GitMerge, UserPlus, BadgeCheck, AlertTriangle, GitBranch, BarChart3, Lock, Ticket, Settings, ClipboardList, ChevronDown, ChevronRight, Sun, Moon, Upload, Crown, Zap, Menu, X, ArrowRightLeft, Database, ScrollText } from "lucide-react";
import { useAuth, canNamed } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import ChangePasswordModal from "./ChangePasswordModal";
import AskJarvisModal from "./AskJarvisModal";
import NotificationBell from "./NotificationBell";
import api from "../api/axios";

const testId = (label) => label.toLowerCase().replace(/ /g, "-");

const navItems = (user) => {
  const role = user?.role;
  const teams = user?.teams || [];
  const isAdmin = role === "admin";
  const isPerformer = role === "performer_agent" || teams.includes("Performer");
  const isCollection = role === "collection_agent" || teams.includes("Collection");
  const isMarketing = role === "marketing" || teams.includes("Marketing");
  const isFinance = role === "finance";
  const isIt = role === "it";
  const isTeamLeader = user?.is_team_leader === true;
  const isHod = user?.role_id === "hod";
  const hasUniquePeople = canNamed(user, "reports.view_unique_people");
  const canRolesManage = canNamed(user, "roles.manage");

  // Finance items — gated purely by named perms, no role-string or team conditions.
  // Each item is independent; all items are appended to the general list at return
  // time (line: return [...items, ...financeItems]).  There is no early return for
  // finance users: operational items are already gated by canLeadWork/isAdmin, so a
  // finance-only user naturally receives a sparse operational list plus their finance
  // items.  A user on any operational team who also holds a finance permission gets
  // their full operational sidebar with the finance item(s) appended.
  const financeItems = [];
  if (canNamed(user, "finance.view_revenue"))  financeItems.push({ to: "/finance",            icon: <Wallet size={18} />,     label: "Finance Dashboard"    });
  if (canNamed(user, "payments.verify"))       financeItems.push({ to: "/finance/payments",   icon: <Receipt size={18} />,    label: "Payment Audit"        });
  if (canNamed(user, "finance.view_revenue"))  financeItems.push({ to: "/finance/gst",        icon: <FileText size={18} />,   label: "GST Report"           });
  if (canNamed(user, "refunds.view_all"))      financeItems.push({ to: "/finance/refunds",    icon: <RefreshCcw size={18} />, label: "Refund Tracker",       badge: "refund_tracker" });
  if (canNamed(user, "payments.approve"))      financeItems.push({ to: "/finance-operations", icon: <BadgeCheck size={18} />, label: "Payment Verification", badge: "payment_verification" });
  if (isIt) {
    return [
      { to: "/it", icon: <Wrench size={18} />, label: "IT Dashboard" },
    ];
  }

  const canLeadWork = isAdmin || isCollection || isPerformer || isMarketing;

  const items = [
    { to: "/", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
  ];
  if (canLeadWork) {
    items.push({ to: "/all-leads", icon: <List size={18} />, label: "All Leads" });
  }

  // ── Group 1: Leads — viewing / working leads (telecaller day-to-day) ──
  const leadsChildren = [];
  if (canLeadWork) {
    leadsChildren.push({ to: "/leads-board", icon: <UserPlus size={18} />, label: "Leads" });
    leadsChildren.push({ to: "/customers", icon: <BadgeCheck size={18} />, label: "Customers" });
  }
  leadsChildren.push({ to: "/my-bucket", icon: <Lock size={18} />, label: "My Bucket" });
  leadsChildren.push({ to: "/my-data", icon: <Database size={18} />, label: "My Data" });
  if (canLeadWork) {
    leadsChildren.push({ to: "/leads", icon: <Users size={18} />, label: "Collection Leads" });
  }
  if (isAdmin || isPerformer) {
    leadsChildren.push({ to: "/performer", icon: <UserCheck size={18} />, label: "Performer Leads" });
  }
  if (canLeadWork) {
    leadsChildren.push({ to: "/failed-payments", icon: <AlertTriangle size={18} />, label: "Incomplete Payments" });
  }
  if (leadsChildren.length) {
    items.push({ group: true, key: "leads", icon: <Users size={18} />, label: "Leads", children: leadsChildren });
  }

  // ── Group 2: Operations — distribution / refunds / alignment ──
  const opsChildren = [
    { to: "/claims", icon: <Ticket size={18} />, label: "Claim Attendee" },
    { to: "/refund-requests", icon: <RefreshCcw size={18} />, label: "Refund Requests" },
  ];
  if (isAdmin) {
    opsChildren.push({ to: "/operations/new-tokens", icon: <KeyRound size={18} />, label: "New Tokens" });
    opsChildren.push({ to: "/operations/assign-leads", icon: <RefreshCcw size={18} />, label: "Assign Leads" });
  }
  if (canNamed(user, "discounts.approve") || canNamed(user, "plans.approve")) {
    opsChildren.push({ to: "/operations/approvals", icon: <BadgeCheck size={18} />, label: "Approvals", badge: "approvals" });
  }
  if (isAdmin) {
    opsChildren.push({ to: "/pending-alignment", icon: <GitBranch size={18} />, label: "Assign Payment", badge: "assign_payment" });
    opsChildren.push({ to: "/merge-reviews", icon: <GitMerge size={18} />, label: "Duplicate Review Queue", badge: "merge_reviews" });
  }
  items.push({ group: true, key: "operations", icon: <ClipboardList size={18} />, label: "Operations", children: opsChildren });

  if (isCollection || isPerformer || isMarketing) {
    items.push({ to: "/my-performance", icon: <Trophy size={18} />, label: "My Performance" });
  }
  if (isTeamLeader) {
    items.push({ to: "/team-dashboard", icon: <Crown size={18} />, label: "Team Dashboard" });
    items.push({ to: "/lead-transfers", icon: <ArrowRightLeft size={18} />, label: "Lead Transfers", badge: "transfers" });
  }
  if (isAdmin) {
    items.push({ to: "/lead-transfers", icon: <ArrowRightLeft size={18} />, label: "Lead Transfers", badge: "transfers" });
    items.push({ to: "/admin", icon: <Shield size={18} />, label: "Admin" });
  }
  // RBAC: assignment.assign_others (HOD/CRO etc.) unlocks the transfer flow
  if (!isTeamLeader && !isAdmin && canNamed(user, "assignment.assign_others")) {
    items.push({ to: "/lead-transfers", icon: <ArrowRightLeft size={18} />, label: "Lead Transfers", badge: "transfers" });
  }
  // Security dashboard — gated by the security.view named permission
  if (canNamed(user, "security.view")) {
    items.push({ to: "/security", icon: <ScrollText size={18} />, label: "Security" });
  }
  // Roles & Permissions: only for users who hold roles.manage (super_admin; NOT sub_admin)
  if (canRolesManage) {
    items.push({ to: "/roles", icon: <Lock size={18} />, label: "Roles & Permissions" });
  }
  if (isAdmin) {
    items.push({ to: "/settings", icon: <Settings size={18} />, label: "Settings" });
  }
  // Reporting: any user with a report-level named perm (unique-people / team / agent performance)
  const hasReportingAccess = hasUniquePeople
    || canNamed(user, "reports.team_performance")
    || canNamed(user, "reports.view_agent_performance");
  if (hasReportingAccess) {
    items.push({ to: "/reporting", icon: <BarChart3 size={18} />, label: "Reporting" });
  }
  if (isAdmin) {
    items.push({ to: "/routing-preview", icon: <Zap size={18} />, label: "Routing Engine" });
  }
  if (hasUniquePeople) {
    items.push({ to: "/coach-overlap", icon: <GitMerge size={18} />, label: "Coach Overlap" });
  }
  if (isAdmin && hasUniquePeople) {
    items.push({ to: "/unique-people", icon: <Users size={18} />, label: "Unique People" });
  }
  if (isAdmin) {
    items.push({ group: true, key: "import", icon: <Upload size={18} />, label: "Import", children: [
      { to: "/current-customer", icon: <BadgeCheck size={18} />, label: "Current Customer" },
      { to: "/extended-import", icon: <FileSpreadsheet size={18} />, label: "Extended Import" },
      { to: "/participant-import", icon: <UserPlus size={18} />, label: "Participant Import" },
      { to: "/lead-assignment", icon: <UserCheck size={18} />, label: "Lead Assignment" },
      { to: "/status-import", icon: <FileText size={18} />, label: "Status Import" },
    ] });
  }
  return [...items, ...financeItems];
};

function NavGroup({ item, groupBadge = 0 }) {
  const location = useLocation();
  const hasActive = item.children.some(
    (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")
  );
  const [open, setOpen] = useState(hasActive);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);

  return (
    <div className="nav-group">
      <button
        type="button"
        className={`nav-link nav-group-toggle ${hasActive ? "has-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${item.key}`}
        style={{ position: "relative" }}
      >
        {item.icon}
        <span>{item.label}</span>
        {groupBadge > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 32,
            background: "#dc2626", color: "#fff", borderRadius: "50%",
            minWidth: 16, height: 16, fontSize: 9, fontWeight: 700,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, padding: "0 3px",
          }} data-testid={`sidebar-group-badge-${item.key}`}>
            {groupBadge > 99 ? "99+" : groupBadge}
          </span>
        )}
        {open
          ? <ChevronDown size={15} className="nav-group-chevron" />
          : <ChevronRight size={15} className="nav-group-chevron" />}
      </button>
      {open && (
        <div className="nav-group-children">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) => `nav-link nav-child ${isActive ? "active" : ""}`}
              data-testid={`nav-${testId(c.label)}`}
              style={{ position: "relative" }}
            >
              {c.icon}
              <span>{c.label}</span>
              {typeof c.badge === "number" && c.badge > 0 && (
                <span style={{
                  position: "absolute", top: 4, right: 8,
                  background: "#dc2626", color: "#fff", borderRadius: "50%",
                  minWidth: 16, height: 16, fontSize: 9, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, padding: "0 3px",
                }} data-testid={`sidebar-badge-${c._badgeKey || c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {c.badge > 99 ? "99+" : c.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showJarvis, setShowJarvis] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [pendingCounts, setPendingCounts] = useState({});

  const fetchCounts = useCallback(() => {
    api.get("/sidebar/pending-counts")
      .then((r) => setPendingCounts(r.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => { const t = setInterval(fetchCounts, 60000); return () => clearInterval(t); }, [fetchCounts]);
  useEffect(() => { fetchCounts(); }, [location.pathname, fetchCounts]);

  // Inject badge counts into nav items (top-level + group children)
  const rawItems = navItems(user);
  const _inject = (item) => {
    if (item.badge && typeof item.badge === "string") {
      return { ...item, badge: pendingCounts[item.badge] || 0, _badgeKey: item.badge };
    }
    return item;
  };
  const items = rawItems.map((item) => {
    if (item.group && item.children) {
      return { ...item, children: item.children.map(_inject) };
    }
    return _inject(item);
  });

  // Compute group badge sums for Operations and Finance
  const groupBadges = {};
  for (const item of items) {
    if (!item.group || !item.children) continue;
    const sum = item.children.reduce((acc, c) => {
      const key = c._badgeKey;
      return acc + (key ? (pendingCounts[key] || 0) : 0);
    }, 0);
    if (sum > 0) groupBadges[item.key] = sum;
  }

  const closeOverlay = () => setMoreOpen(false);
  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // Focus Mode is available for telecallers, team leaders, HODs (mobile-first feature)
  const role = user?.role;
  const teams = user?.teams || [];
  const isAdmin = role === "admin";
  const isHod = user?.role_id === "hod";
  const isTeamLeader = user?.is_team_leader === true;
  const isTelecaller = role === "collection_agent" || role === "performer_agent" || role === "marketing"
    || teams.length > 0;
  const canFocusMode = isAdmin || isHod || isTeamLeader || isTelecaller;

  // Determine if user can work with leads (for bottom-nav items)
  const isPerformer = role === "performer_agent" || teams.includes("Performer");
  const isCollection = role === "collection_agent" || teams.includes("Collection");
  const isMarketing = role === "marketing" || teams.includes("Marketing");
  const canLeadWork = isAdmin || isCollection || isPerformer || isMarketing;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="app-shell">
      {/* Sidebar — desktop */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">S</div>
          <div style={{ flex: 1 }}>
            <div className="brand-name">Snehworld</div>
            <div className="brand-sub">CRM</div>
          </div>
          <button
            className="logout-btn"
            onClick={toggleTheme}
            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            data-testid="theme-toggle-btn"
          >
            {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
          </button>
          <NotificationBell />
        </div>
        <nav className="sidebar-nav">
          {items.map((item) => (
            item.group ? (
              <NavGroup key={item.key} item={item} groupBadge={groupBadges[item.key] || 0} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${testId(item.label)}`}
                style={{ position: "relative" }}
              >
                {item.icon}
                <span>{item.label}</span>
                {typeof item.badge === "number" && item.badge > 0 && (
                  <span style={{
                    position: "absolute", top: 4, right: 8,
                    background: "#dc2626", color: "#fff", borderRadius: "50%",
                    minWidth: 16, height: 16, fontSize: 9, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, padding: "0 3px",
                  }} data-testid={`sidebar-badge-${item._badgeKey || "transfers"}`}>
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </NavLink>
            )
          ))}
          <button
            type="button"
            className="nav-link nav-link-jarvis"
            onClick={() => setShowJarvis(true)}
            data-testid="nav-ask-jarvis"
          >
            <Bot size={18} />
            <span>Ask Jarvis</span>
            <span className="nav-badge">AI</span>
          </button>
          {canFocusMode && (
            <NavLink
              to="/focus"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-focus-mode"
              style={{ marginTop: 4, background: "var(--accent-muted, rgba(245,158,11,0.1))", borderRadius: 8 }}
            >
              <Zap size={18} />
              <span>Focus Mode</span>
            </NavLink>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{user?.name?.charAt(0) || "U"}</div>
            <div>
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{user?.rbac?.role_display || user?.team_type}</div>
            </div>
          </div>
          <div className="sidebar-actions">
            <button
              className="logout-btn"
              onClick={() => setShowChangePwd(true)}
              title="Change password"
              data-testid="change-password-btn"
            >
              <KeyRound size={15} />
            </button>
            <button className="logout-btn" onClick={handleLogout} title="Logout" data-testid="logout-btn">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="main-area">
        {/* Mobile top bar */}
        <header className="topbar">
          <div className="topbar-brand">
            <div className="brand-mark sm">S</div>
            <span className="topbar-title">Snehworld CRM</span>
          </div>
          <div className="topbar-right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <NotificationBell compact />
            <button className="logout-btn" onClick={handleLogout} title="Sign out" data-testid="mobile-topbar-logout-btn">
              <LogOut size={16} />
            </button>
            <button className="logout-btn" onClick={toggleTheme} title="Toggle theme" data-testid="mobile-theme-toggle-btn">
              {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
            </button>
            <span className="user-pill">{user?.name?.split(" ")[0]}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="main-content">
          {children}
        </main>
      </div>

      {/* Bottom nav — mobile: 4 fixed items + More overlay */}
      <nav className="bottom-nav" data-testid="mobile-bottom-nav">
        <NavLink to="/" end
          className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}
          data-testid="bottom-nav-dashboard">
          <LayoutDashboard size={20} />
          <span>Home</span>
        </NavLink>
        {canLeadWork && (
          <NavLink to="/all-leads"
            className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}
            data-testid="bottom-nav-leads">
            <List size={20} />
            <span>Leads</span>
          </NavLink>
        )}
        {canLeadWork && (
          <NavLink to="/customers"
            className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}
            data-testid="bottom-nav-customers">
            <BadgeCheck size={20} />
            <span>Customers</span>
          </NavLink>
        )}
        {canFocusMode && (
          <NavLink to="/focus"
            className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}
            style={({ isActive }) => isActive ? { color: "var(--accent)", borderTop: "2px solid var(--accent)" } : {}}
            data-testid="bottom-nav-focus-mode">
            <Zap size={20} />
            <span>Focus</span>
          </NavLink>
        )}
        <button
          className="bottom-nav-item"
          onClick={() => setMoreOpen(true)}
          data-testid="mobile-more-btn">
          <Menu size={20} />
          <span>More</span>
        </button>
      </nav>

      {/* Full-screen overlay menu */}
      {moreOpen && (
        <div className="mobile-overlay" data-testid="mobile-overlay">
          {/* Header */}
          <div className="mobile-overlay-header">
            <div className="brand-mark sm">S</div>
            <span style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16 }}>Menu</span>
            <button className="logout-btn" onClick={closeOverlay} data-testid="mobile-overlay-close">
              <X size={22} />
            </button>
          </div>

          {/* User info strip */}
          <div className="mobile-overlay-user">
            <div className="user-avatar" style={{ width: 44, height: 44, fontSize: 18, flexShrink: 0 }}>
              {user?.name?.charAt(0) || "U"}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>{user?.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{user?.team_type || user?.role}</div>
            </div>
          </div>

          {/* Nav items */}
          <div className="mobile-overlay-nav">
            {items.map((item) => {
              if (item.group) {
                return (
                  <div key={item.key} className="mobile-nav-group">
                    <button
                      className="mobile-nav-group-header"
                      onClick={() => toggleGroup(item.key)}
                      data-testid={`mobile-group-${item.key}`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      {openGroups[item.key]
                        ? <ChevronDown size={15} style={{ marginLeft: "auto" }} />
                        : <ChevronRight size={15} style={{ marginLeft: "auto" }} />}
                    </button>
                    {openGroups[item.key] && (
                      <div className="mobile-nav-group-children">
                        {item.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            onClick={closeOverlay}
                            className={({ isActive }) => `mobile-nav-item mobile-nav-child ${isActive ? "active" : ""}`}
                            data-testid={`mobile-nav-${testId(c.label)}`}
                          >
                            {c.icon}
                            <span>{c.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              // Skip items already visible in bottom nav
              const BOTTOM_ROUTES = ["/", "/all-leads", "/customers", "/focus"];
              if (BOTTOM_ROUTES.includes(item.to)) return null;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={closeOverlay}
                  className={({ isActive }) => `mobile-nav-item ${isActive ? "active" : ""}`}
                  data-testid={`mobile-nav-${testId(item.label)}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            <div className="mobile-nav-divider" />

            <button className="mobile-nav-item" onClick={() => { setShowJarvis(true); closeOverlay(); }} data-testid="mobile-overlay-jarvis">
              <Bot size={18} />
              <span>Ask Jarvis</span>
            </button>
            <button className="mobile-nav-item" onClick={() => { setShowChangePwd(true); closeOverlay(); }} data-testid="mobile-overlay-password">
              <KeyRound size={18} />
              <span>Change Password</span>
            </button>
          </div>

          {/* Footer: Sign out */}
          <div className="mobile-overlay-footer">
            <button className="mobile-overlay-logout" onClick={handleLogout} data-testid="mobile-overlay-logout">
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
      {showJarvis && <AskJarvisModal user={user} onClose={() => setShowJarvis(false)} />}
    </div>
  );
}
